import React, { useState, useEffect } from "react";
import { useV1ProductionWorkCentersList } from "@/api/generated/v1/v1";
import { useDowntimeReasonCodesList, useStartDowntime } from "@/features/shopfloor/api";

export interface BlockedLotDowntimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  lotId?: string;
  lotNumber: string;
  responsibleDepartment?: string;
  reasonName?: string;
  suggestedDowntimeCode?: string;
  defaultWorkCenterId?: string;
  onSuccess?: () => void;
}

export function BlockedLotDowntimeModal({
  isOpen,
  onClose,
  lotId,
  lotNumber,
  responsibleDepartment,
  reasonName,
  suggestedDowntimeCode,
  defaultWorkCenterId = "",
  onSuccess,
}: BlockedLotDowntimeModalProps) {
  const { data: workCentersData } = useV1ProductionWorkCentersList();
  const workCenters = workCentersData?.results || [];
  const { data: reasons = [] } = useDowntimeReasonCodesList();
  const startDowntime = useStartDowntime();

  const [workCenterId, setWorkCenterId] = useState(defaultWorkCenterId);
  const [reasonCodeId, setReasonCodeId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (defaultWorkCenterId) {
      setWorkCenterId(defaultWorkCenterId);
    } else if (workCenters.length > 0 && !workCenterId && workCenters[0]?.id) {
      setWorkCenterId(workCenters[0].id);
    }
  }, [defaultWorkCenterId, workCenters, workCenterId]);

  useEffect(() => {
    if (suggestedDowntimeCode && reasons.length > 0 && !reasonCodeId) {
      const match = reasons.find(
        (r) => r.code.toLowerCase() === suggestedDowntimeCode.toLowerCase()
      );
      if (match) {
        setReasonCodeId(match.id);
      }
    }
  }, [suggestedDowntimeCode, reasons, reasonCodeId]);

  useEffect(() => {
    const defaultNote = `توقف خط به دلیل تعلیق پالت ${lotNumber} (واحد مسئول: ${responsibleDepartment || "انبار"}${reasonName ? ` - علت: ${reasonName}` : ""})`;
    setNotes(defaultNote);
  }, [lotNumber, responsibleDepartment, reasonName]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!workCenterId) {
      setError("انتخاب ایستگاه کاری الزامی است.");
      return;
    }
    if (!reasonCodeId) {
      setError("انتخاب علت توقف الزامی است.");
      return;
    }
    try {
      await startDowntime.mutateAsync({
        work_center: workCenterId,
        reason_code: reasonCodeId,
        related_lot: lotId,
        notes,
      });
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onSuccess?.();
        onClose();
      }, 1000);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || "خطا در ثبت توقف.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="blocked-lot-downtime-modal"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          padding: "20px",
          width: "100%",
          maxWidth: "480px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h3 style={{ margin: "0 0 12px", fontSize: "1.125rem", color: "#1F2937" }}>
          🛑 ثبت توقف ایستگاه کاری (Downtime)
        </h3>

        {isSuccess ? (
          <div data-testid="downtime-success-msg" style={{ padding: "16px", background: "#ECFDF5", color: "#065F46", borderRadius: "6px", textAlign: "center" }}>
            ✓ توقف ایستگاه کاری با موفقیت ثبت شد.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontSize: "0.875rem" }}>
                ایستگاه کاری:
              </label>
              <select
                data-testid="downtime-workcenter-select"
                value={workCenterId}
                onChange={(e) => setWorkCenterId(e.target.value)}
                style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #D1D5DB" }}
              >
                <option value="">— انتخاب ایستگاه کاری —</option>
                {workCenters.map((wc) => (
                  <option key={wc.id} value={wc.id}>
                    {wc.name} ({wc.code})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontSize: "0.875rem" }}>
                علت توقف:
              </label>
              <select
                data-testid="downtime-reason-select"
                value={reasonCodeId}
                onChange={(e) => setReasonCodeId(e.target.value)}
                style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #D1D5DB" }}
              >
                <option value="">— انتخاب علت توقف —</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} - {r.description}
                  </option>
                ))}
              </select>
              {suggestedDowntimeCode && (
                <div style={{ fontSize: "0.75rem", color: "#6B7280", marginTop: "2px" }}>
                  کد پیشنهادی واحد مسئول: <strong>{suggestedDowntimeCode}</strong>
                </div>
              )}
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontSize: "0.875rem" }}>
                یادداشت توقف (شامل شماره پالت):
              </label>
              <textarea
                data-testid="downtime-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #D1D5DB", boxSizing: "border-box" }}
              />
            </div>

            {error && (
              <div data-testid="downtime-error-msg" style={{ padding: "8px", background: "#FEF2F2", color: "#991B1B", borderRadius: "6px", marginBottom: "12px", fontSize: "0.875rem" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                type="button"
                data-testid="downtime-cancel-btn"
                onClick={onClose}
                disabled={startDowntime.isPending}
                style={{ padding: "8px 16px", borderRadius: "6px", border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}
              >
                انصراف
              </button>
              <button
                type="submit"
                data-testid="downtime-submit-btn"
                disabled={startDowntime.isPending || !reasonCodeId || !workCenterId}
                style={{
                  padding: "8px 16px",
                  borderRadius: "6px",
                  border: "none",
                  background: startDowntime.isPending || !reasonCodeId || !workCenterId ? "#9CA3AF" : "#DC2626",
                  color: "#fff",
                  cursor: startDowntime.isPending || !reasonCodeId || !workCenterId ? "not-allowed" : "pointer",
                }}
              >
                {startDowntime.isPending ? "در حال ثبت..." : "تأیید و ثبت توقف"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
